import { flow, Instance, SnapshotIn, SnapshotOut, types } from 'mobx-state-tree';
import { IProductModel, ProductModel } from './product';
import Datadog from '../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS, getRangePhotoLoadingTimeAction } from '../../_shared/datadog/DatadogCustomActions';
import { PickedImage, SortOrder } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { logSDKError, logWarning } from 'src/_shared/logger';
import { IUserImageModel } from './userImage';
import { pickedImagesToString } from '../../_shared/utils/pickedImagesToString';
import { handleGlobalError } from '../../_shared/utils/handle-global-error/handleGlobalError';
import { ThumbnailImageModel } from './thumbnailImage';
import { GroupModel, IGroupModel } from './group';
import { SAVING_STATE } from '../page-project/pageProject.constants';
import { IReactionDisposer, reaction } from 'mobx';
import { BandInfo, BoxStyleInfo, FormatInfo, LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { CanvasBorderType } from '@photosi/expo-photosi-sdk/build/ImageEditor';
import { TProductOptionChoiceConfigurationIdsMap } from './productOptionChoice';
import { ProductReplacement, UpgradeSetInfo } from '@photosi/expo-photosi-sdk/build/Project';
import { PriceDetailsModel } from './priceDetails';
import { BaseProductsOptionsModel } from './_base/baseProductsOptionsModel';
import { DYNAMIC_MODAL_VIEWS } from './dynamic-modal-layer/dynamicModalLayer.constants';
import { ImageData } from '@photosi/expo-photosi-sdk/build/Project';
import { PREVIEW_PRODUCT_TYPES } from './preview.constants';

export type TProjectModel = typeof ProjectModel;
export type ProjectModelInstance = Instance<TProjectModel>;
export interface IProjectModelIn extends SnapshotIn<TProjectModel> {}
export interface IProjectModelOut extends SnapshotOut<TProjectModel> {}
export interface IProjectModel extends ProjectModelInstance {}

export const ProjectModel = BaseProductsOptionsModel.named('ProjectModel')
  .props({
    id: types.identifier,
    name: types.optional(types.string, ''),
    estimate: types.maybe(types.number),
    original: types.maybe(types.number),
    products: types.optional(types.array(ProductModel), []),
    cartId: types.optional(types.string, ''),
    addingProductCount: types.optional(types.number, 0),
    alreadyAddedProductCount: types.optional(types.number, 0),
    productsCount: types.optional(types.number, 0),
    thumbnailImages: types.optional(types.array(ThumbnailImageModel), []),
    groups: types.optional(types.array(GroupModel), []),
    groupTypes: types.optional(types.array(GroupModel), []),
    priceDetails: types.maybe(PriceDetailsModel),
    hasShareToken: types.maybe(types.boolean),
  })
  .views(self => ({
    get totalNumberOfProducts() {
      return self.productsCount;
    },
    get totalNumberOfUserImages() {
      return self.products.reduce((partialSum, product) => partialSum + product.totalNumberOfUserImages, 0);
    },
    get isAnyBlankSpace() {
      // Itera sull'array dei prodotti e verifica se almeno uno
      // ha una blank space, utilizzando il getter definito a livello di prodotto
      return self.products.some(product => product.isAnyBlankSpace);
    },
    get isMissingMaxProducts() {
      return self.groups.some(group => group.isMissingMaxProducts);
    },
    get isMissingMinProducts() {
      // Itera sull'array dei gruppi e verifica se almeno uno ha dei prodotti mancanti
      return self.groups.some(group => group.isMissingMinProducts);
    },
    get firstBlankProductIndex() {
      // Trova l'indice del primo prodotto che soddisfa la condizione
      // isAnyBlankSpace definita nel modello del prodotto
      return self.products.findIndex(product => product.isAnyBlankSpace);
    },
    get isAnyQualityAlert() {
      return self.products.some(product =>
        product.preview.userImageAreas.some(userImageArea => !!userImageArea.userImage?.qualityAlert),
      );
    },
    get userImagesWithQualityAlert() {
      const userImages: IUserImageModel[] = [];

      self.products.forEach(product => {
        if (product.preview.userImageAreas.some(userImageArea => !!userImageArea.userImage?.qualityAlert)) {
          const userImageAreas = product.preview.userImageAreas;
          userImageAreas
            .filter(userImageArea => !!userImageArea.userImage?.qualityAlert)
            .forEach(userImageArea => {
              if (!!userImageArea.userImage) userImages.push(userImageArea.userImage);
            });
        }
      });
      return userImages;
    },
    get userImageAreasWithCropAlert() {
      return self.products.flatMap(product => product.userImageAreasWithCropAlert);
    },
    get isAddingOneNewProduct() {
      return self.addingProductCount === 1;
    },
    get isAddingSomeNewProducts() {
      return self.addingProductCount > 1;
    },
    get filteredGroups() {
      return self.groups.filter(group => !group.isEmpty);
    },
    get selectedProducts() {
      return self.products.filter(product => product.isSelected);
    },
    get selectedThumbnails() {
      return self.thumbnailImages.filter(thumbnail => thumbnail.isSelected);
    },
    get isProductSetProject() {
      return self.products.length && self.products[0].type === PREVIEW_PRODUCT_TYPES.FRAME_ADHESIVE;
    },
  }))
  .views(self => ({
    get isEmpty() {
      return self.totalNumberOfProducts === 0;
    },
    get hasAnyImageWithCropAlert(): boolean {
      return self.userImageAreasWithCropAlert.length > 0;
    },
  }))
  .volatile<{
    lastSaveTimestamp: string;
    savingState: SAVING_STATE;
    zoomedImage: string | undefined;
    gridColumns: number;
    frameConfigStepsData: LookupChoiceForStep[];
    upgradeSetInfo: UpgradeSetInfo | undefined;
  }>(self => ({
    lastSaveTimestamp: '',
    savingState: SAVING_STATE.toBeSaved,
    zoomedImage: undefined,
    gridColumns: 5,
    frameConfigStepsData: [],
    upgradeSetInfo: undefined,
  }))
  .actions(self => ({
    setAddingProductCount(value: number) {
      self.addingProductCount = value;
    },
    setAlreadyAddedProductCount(value: number) {
      self.alreadyAddedProductCount = value;

      // every time you change the alreadyAddedProductCount, update the productsCount
      self.productsCount = self.products.length;
    },
    setSavingState(savingState: SAVING_STATE) {
      self.savingState = savingState;
    },
    setLastSaveTimestamp(timestamp: string) {
      self.lastSaveTimestamp = timestamp;
    },
    setZoomedImage(id: string) {
      self.zoomedImage = id;
    },
    resetZoomedImage() {
      self.zoomedImage = undefined;
    },
    setUpgradeSetInfo(info: UpgradeSetInfo | undefined) {
      self.upgradeSetInfo = info;
    },
  }))
  .actions(self => ({
    startAddingProducts(count: number) {
      self.setAddingProductCount(count);
      self.setAlreadyAddedProductCount(0);
    },
    completeAddingProducts() {
      self.setAddingProductCount(0);
      self.setAlreadyAddedProductCount(0);
      if (self.root.dynamicModal.activeModal?.isUploadingPhoto) {
        self.root.dynamicModal.activeModal?.setIsUploadingPhoto(false);
      }
    },
  }))
  .actions(self => {
    /**
     * Adds products to project
     */
    const addProducts = flow(function* addProducts(productConfigurationId: string, files: PickedImage[]) {
      const addingProductsCount = files?.length ?? 0;

      if (addingProductsCount === 0 || !self.sdkProject) return;

      const fileSizeAvailable = files.every(f => f.file && f.file.size > 0);
      const totalSizeBytes = files.map(f => (f.file ? f.file.size : 0)).reduce((a, b) => a + b, 0);
      if (fileSizeAvailable) {
        Datadog.startMeasure(getRangePhotoLoadingTimeAction(totalSizeBytes));
      }
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.photo_loading_time);
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.photo_loading_time_on_server);
      self.root.data.startPhotoPickerToFirstImageMeasure(addingProductsCount);

      if (addingProductsCount > 1) {
        Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.frame_loading_time_with_photos);
      } else if (addingProductsCount === 1) {
        Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.frame_addition_time);
      }

      self.startAddingProducts(addingProductsCount);

      return handleGlobalError(self.sdkProject.addProducts(productConfigurationId, files))
        .then(() => {
          // after added all products
          if (fileSizeAvailable) {
            Datadog.endMeasure(getRangePhotoLoadingTimeAction(totalSizeBytes));
          }

          const imagesLoaded = files.map(({ file }) => ({
            type: file?.type ?? 'heic',
            size: file?.size ? file.size / 1000000 : 0, // in MB
          }));
          Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.photo_loading_time_on_server, {
            imagesLoaded,
          });
        })
        .catch(error => {
          logSDKError(error, pickedImagesToString(files));

          // remove empty products
          self.products
            .filter(product => product.totalNumberOfUserImages === 0)
            .forEach(productToRemove => {
              productToRemove.remove();
            });

          self.root.toastStore.showErrorToast({
            id: 'alert-uploading-photos-error-toast',
            message:
              addingProductsCount > 1
                ? self.root.i18n.t('toast:uploading-photos-error')
                : self.root.i18n.t('toast:uploading-photo-error'),
          });

          if (fileSizeAvailable) {
            Datadog.cleanMeasure(getRangePhotoLoadingTimeAction(totalSizeBytes));
          }
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.photo_loading_time);
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.photo_loading_time_on_server);
          self.root.data.cleanPhotoPickerToFirstImageMeasure();
          if (addingProductsCount > 1) {
            Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.frame_loading_time_with_photos);
          } else if (addingProductsCount === 1) {
            Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.frame_addition_time);
          }
        })
        .then(() => {
          // after adding products with success or error
          self.completeAddingProducts();
        });
    });

    const setName = (value: string) => {
      try {
        self.sdkProject?.setName(value);
      } catch (error) {
        logSDKError(error, { name: value });
      }
    };

    const getUpsellingInfo = flow(function* getUpsellingInfo() {
      return self.sdkProject.getCrossUpSellingInfo();
    });

    const addImages = flow(function* addImages(images: PickedImage[], googleAccessToken?: string | undefined) {
      if (self.root.data.uploadError) self.root.data.setUploadError(false);
      self.root.dynamicModal?.activeModal?.setIsUploadingPhoto(true);
      self.root.data.startPhotoPickerToFirstImageMeasure(images.length);
      try {
        yield self.sdkProject.addImages(images, googleAccessToken);
      } catch (error) {
        self.root.data.cleanPhotoPickerToFirstImageMeasure();
        throw error;
      }
    });

    const deleteImages = flow(function* deleteImages(imageIds: string[]) {
      yield self.sdkProject.deleteImages(imageIds);
    });

    const loadThumbnailImages = flow(function* loadThumbnailImages() {
      if (self.sdkProject) {
        const images = yield self.sdkProject.getImages();
        images.forEach((image: ImageData) => {
          self.thumbnailImages
            .find(thumb => thumb.originalImageId === image.originalImageId)
            ?.setUsageQuantity(image.usageQuantity);
        });
      }
    });

    const selectAllThumbnailImages = () => {
      self.thumbnailImages.forEach(t => t.select());
    };

    const deselectAllThumbnailImages = () => {
      self.selectedThumbnails.forEach(t => t.deselect());
    };

    const getSuggestedFormats = flow(function* getSuggestedFormats(
      groupCode: string,
      productConfigId: string,
      photoIds: string[],
    ) {
      const formats: FormatInfo[] = yield self.sdkProject
        .getPrintUtils()
        .getSuggestedFormats(productConfigId, photoIds, groupCode);
      return formats;
    });

    const getAllFormats = flow(function* getAllFormats(groupCode: string, photoIds: string[]) {
      const formats: FormatInfo[] = yield self.sdkProject.getPrintUtils().getFormats(groupCode, photoIds);
      return formats;
    });

    const getBands = flow(function* getBands(productConfigId: string) {
      const bands: BandInfo[] = yield self.sdkProject.getPrintUtils().getBands(productConfigId);
      return bands;
    });

    const getBoxStyles = flow(function* getBoxStyles(productConfigId: string, groupCode: string) {
      const boxes: BoxStyleInfo[] = yield self.sdkProject.getPrintUtils().getBoxStyles(productConfigId, groupCode);
      return boxes;
    });

    const getUpgradeSetInfo = flow(function* getUpgradeSetInfo() {
      const info: UpgradeSetInfo = yield self.sdkProject.getUpgradeSetInfo();
      return info;
    });

    const switchToUpgradeSet = flow(function* switchToUpgradeSet(productId: string) {
      const freshInfo: UpgradeSetInfo = yield self.sdkProject.getUpgradeSetInfo();
      if (!freshInfo) return;
      self.setUpgradeSetInfo(freshInfo);
      self.root.project.setProductConfigurationId(freshInfo.productConfigurationId);
      yield self.sdkProject.replaceProduct(productId, freshInfo.productConfigurationId);
    });

    const deactivateCropAlert = flow(function* deactivateCropAlert(userImageIds: string[]) {
      if (userImageIds.length > 0) {
        yield self.sdkProject.deactivateCropAlert(userImageIds);
      }
    });

    const deactivateQualityAlert = flow(function* deactivateQualityAlert(userImageIds: string[]) {
      if (userImageIds.length > 0) {
        yield self.sdkProject.deactivateQualityAlert(userImageIds);
      }
    });

    const getAllFormatsList = flow(function* getAllFormatsList(groupCode: string) {
      const formats: FormatInfo[] = yield self.sdkProject.getPrintUtils().getAllFormats(groupCode);
      return formats;
    });

    const getDefaultProductIdByGroup = flow(function* getDefaultProductIdByGroup(group?: IGroupModel) {
      let productConfigurationId: string | undefined;
      try {
        if (group && group.code && group.groupInfo?.length > 0) {
          productConfigurationId = yield self.sdkProject
            .getPrintUtils()
            .getDefaultProductByGrouping(group.code, group.groupInfo[0], group.productConfigurationIdDefault);
        }
      } catch (error) {
        logSDKError(error, { groupCode: group?.code, format: group?.groupInfo?.[0] });
      }

      return productConfigurationId;
    });

    const getPaperTypes = flow(function* getPaperTypes(groupCode: string, productConfigId: string) {
      const paperTypes: LookupChoiceForStep[] = yield self.sdkProject
        .getPrintUtils()
        .getPaperTypes(groupCode, productConfigId);
      return paperTypes;
    });

    const getPaperFinish = flow(function* getPaperFinish(groupCode: string, productConfigId: string) {
      const paperFinishes: LookupChoiceForStep[] = yield self.sdkProject
        .getPrintUtils()
        .getPaperFinish(groupCode, productConfigId);
      return paperFinishes;
    });

    const onGridColumnsChange = (value: number) => {
      self.gridColumns = value;
    };

    const getFrameConfigStepsData = flow(function* getFrameConfigStepsData(productConfigurationId: string) {
      if (self.sdkProject) {
        try {
          self.frameConfigStepsData = yield self.sdkProject.getFrameConfigStepsData(productConfigurationId);
        } catch (error) {
          logSDKError(`[getFrameConfigStepsData]: ${error}`, { productConfigurationId });
          self.frameConfigStepsData = [];
        }
      }
      return self.frameConfigStepsData;
    });

    return {
      addProducts,
      setName,
      getUpsellingInfo,
      addImages,
      loadThumbnailImages,
      getSuggestedFormats,
      getAllFormats,
      getBands,
      getBoxStyles,
      selectAllThumbnailImages,
      deselectAllThumbnailImages,
      deactivateCropAlert,
      deleteImages,
      getAllFormatsList,
      getDefaultProductIdByGroup,
      onGridColumnsChange,
      deactivateQualityAlert,
      getUpgradeSetInfo,
      switchToUpgradeSet,
      getPaperTypes,
      getPaperFinish,
      getFrameConfigStepsData,
    };
  })
  .actions(self => ({
    syncGroupsAndProducts() {
      // sync products references in groups
      self.groups.map(group => ({
        ...group,
        products: self.products.filter(product => product.groupId === group.id),
      }));
    },
    selectProduct(productId: string) {
      const product = self.products.find(product => product.id === productId);
      product?.setIsSelected(true);
    },
    selectProductsByIds(productIds: string[]) {
      self.products.forEach(product => {
        product.setIsSelected(productIds.includes(product.id));
      });
    },
    selectProductsByGroupIdAndTag(groupId: string, tag?: string) {
      self.products.forEach(product => {
        product.setIsSelected(product.groupId === groupId);
        // product.setIsSelected(product.groupId === groupId && product.tags.includes(tag));
      });
    },
    deselectProduct(productId: string) {
      const product = self.products.find(product => product.id === productId);
      product?.setIsSelected(false);
    },
    selectAllProducts() {
      self.products.forEach(product => product.setIsSelected(true));
    },
    deselectAllProducts() {
      self.products.forEach(product => product.setIsSelected(false));
    },
    updateProductPriority(productId: string) {
      self.sdkProject.updateOrderIndex(productId, SortOrder.Default);
    },
    resetProductPriority() {
      self.sdkProject.updateOrderIndex(null, SortOrder.Default);
    },
    selectProductsBySmartTag(smartTag: string) {
      self.products.forEach(product => {
        if (product.smartSelectionTags.includes(smartTag)) product.setIsSelected(true);
        else product.setIsSelected(false);
      });
    },
    applyLookupToProductGroup: flow(function* applyLookupToProductGroup(
      products: IProductModel[],
      productConfigurationIdsMap: TProductOptionChoiceConfigurationIdsMap,
      groupCode?: string,
    ) {
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.configuration_option_apply_time);

      // add opacity
      products.forEach(product => product.setIsUpdating(true));

      const productReplacements = products.reduce<ProductReplacement[]>((acc, product) => {
        const productConfigurationId = productConfigurationIdsMap.get(product.id);
        if (productConfigurationId) {
          return [
            ...acc,
            {
              productId: product.id,
              productConfigurationId,
              groupCode,
            } satisfies ProductReplacement,
          ];
        } else {
          logWarning(`[applyLookupToProductGroup] Product ${product.id} doesn't have a configurationId`);
          return acc;
        }
      }, []);
      // apply changes on SDK
      return self.sdkProject?.replaceProducts(productReplacements).catch(error => {
        logSDKError(`[replaceProducts]: ${error}`, { products: products, productConfigurationIdsMap, groupCode });
      });
    }),
    setBackgroundColorsToProductGroup: flow(function* setBackgroundColorsToProductGroup(
      products: IProductModel[],
      backgroundColor: string,
    ) {
      const productIds = products.map(product => product.id);
      return self.sdkProject?.setProductsBackgroundColor(backgroundColor, productIds).catch(error => {
        logSDKError(`[setProductsBackgroundColor]: ${error}`, { products: products, backgroundColor });
      });
    }),
    setBorderToProductGroup: flow(function* setBorderToProductGroup(
      products: IProductModel[],
      productConfigurationIdsMap: TProductOptionChoiceConfigurationIdsMap,
    ) {
      return Promise.all(
        products.map(async (product: IProductModel) => {
          await Promise.all(
            product.userImages.map(userImage => {
              return userImage.setBorder(productConfigurationIdsMap.get(product.id) as CanvasBorderType);
            }),
          );
        }),
      );
    }),
    setProductsUserText: flow(function* setProductsUserText(
      products: IProductModel[],
      text: string,
      size: number,
      font: string,
      color: string,
      anchor: string,
      style: string,
    ) {
      //TODO refactor for more user text areas
      const productIds = products
        .filter(element => element.preview?.userTextArea != undefined)
        .map(product => product.id);
      const idTextArea = products[0].preview.userTextArea?.id;
      return self.sdkProject
        ?.setProductsUserText(idTextArea, text, size, font, color, anchor, style, productIds)
        .catch(error => {
          logSDKError(`[setProductsUserText]: ${error}`, { products: products });
        });
    }),
  }))
  .actions(self => ({
    updateProductGroupQuantity: flow(function* updateProductQuantity(
      group,
      product: IProductModel,
      newQuantity: number,
      ctaCallback,
      undoCallback,
    ) {
      if (group?.isHappyCaseOrPhotoKit) {
        const isAddingProducts = newQuantity > product.quantity;
        const isRemovingProducts = newQuantity < product.quantity;
        const newTotalProducts = group.totalProducts - product.quantity + newQuantity;
        if (typeof group?.maxItems === 'number' && isAddingProducts && newTotalProducts > group.maxItems) {
          self.root.dynamicModal.openModal({
            dynamicModalView: DYNAMIC_MODAL_VIEWS.OverLimitProducts,
            productLimit: group.maxItems,
            groupWithLimit: group.code,
            onPressAddBox: () => {
              const finalQuantity = Math.min(product.quantity, group?.maxItems || 100000);
              ctaCallback(finalQuantity);
              self.root.dynamicModal.closeModal();
            },
            onPressRemoveProducts: () => {
              undoCallback(product.quantity);
              self.root.dynamicModal.closeModal();
            },
          });
        } else if (typeof group?.minItems === 'number' && isRemovingProducts && newTotalProducts < group.minItems) {
          self.root.dynamicModal.openModal({
            dynamicModalView: DYNAMIC_MODAL_VIEWS.UnderMinLimitProducts,
            productLimit: group.minItems,
            groupWithLimit: group.code,
            onPressAddBox: () => {
              const finalQuantity = Math.max(product.quantity, (group?.minItems || 0) - group.totalProducts);
              undoCallback(finalQuantity);
              self.root.dynamicModal.closeModal();
            },
            onPressRemoveProducts: () => {
              const finalQuantity = Math.min(newQuantity, group?.maxItems || 100000);
              ctaCallback(finalQuantity);
              self.root.dynamicModal.closeModal();
            },
          });
        } else {
          ctaCallback(newQuantity);
        }
      } else {
        ctaCallback(newQuantity);
      }
    }),
  }))
  .actions(self => ({
    duplicateProduct: flow(function* duplicateProduct(product: IProductModel) {
      const group = self.groups.find(g => g.id === product.groupId);
      const quantity = product.quantity * 2;
      return new Promise((resolve, reject) =>
        self.updateProductGroupQuantity(
          group,
          product,
          quantity,
          () => {
            product
              .duplicate()
              .then(() => resolve(quantity))
              .catch(reject);
          },
          () => {
            resolve(product.quantity);
          },
        ),
      );
    }),
    updateProductQuantity: flow(function* updateProductQuantity(product: IProductModel, quantity: number) {
      const group = self.groups.find(g => g.id === product.groupId);
      const finalQuantity = yield new Promise(resolve =>
        self.updateProductGroupQuantity(
          group,
          product,
          quantity,
          (newQuantity: number) => {
            product.updateQuantity(quantity);
            resolve(newQuantity);
          },
          (newQuantity: number) => {
            product.updateQuantity(newQuantity);
            resolve(newQuantity);
          },
        ),
      );

      return finalQuantity;
    }),
  }))
  .actions(self => ({
    syncProductsOptions() {
      const productsToUpdate =
        self.selectedProducts.length > 0
          ? self.selectedProducts
          : self.root.project.activeGroup?.products || self.products;
      self.updateProductsOptionsDebounced(productsToUpdate);
    },
  }))
  .actions(self => ({
    manageGroups() {
      // 1. Cleanup: rimuovi gruppi vuoti non-placeholder
      const groupsToRemove = self.groups.filter(group => group.isEmpty);
      groupsToRemove.forEach(group => {
        if (__DEV__) console.log(`[ProjectModel] Removing empty group: ${group.id}`);
        self.groups.remove(group);
      });

      // 2. Aggiorna title suffixes per i gruppi rimanenti
      // Mappa per contare le occorrenze di ogni titolo tradotto
      const titleOccurrences = new Map<string, number>();

      // Prima passata: conta tutte le occorrenze di ogni titolo
      self.groups.forEach(group => {
        const title = self.root.i18n.t(`groups:${group.code}-title`);
        titleOccurrences.set(title, (titleOccurrences.get(title) || 0) + 1);
      });

      // Mappa per tracciare l'indice corrente per ogni titolo duplicato
      const titleCounters = new Map<string, number>();

      // Seconda passata: imposta i suffissi
      self.groups.forEach(group => {
        if (!group.isHappyCaseOrPhotoKit && group.groupInfo) {
          if (group.groupInfo.length > 0) {
            const suffix = group.groupInfo[0];
            group.setTitleSuffix(` ${suffix}`);
          }
        } else {
          const title = self.root.i18n.t(`groups:${group.code}-title`);
          const totalOccurrences = titleOccurrences.get(title) || 1;
          // Se ci sono duplicati, imposta il suffisso
          if (totalOccurrences > 1) {
            const currentIndex = (titleCounters.get(title) || 0) + 1;
            titleCounters.set(title, currentIndex);
            group.setTitleSuffix(` - ${currentIndex}`);
          } else {
            group.setTitleSuffix('');
          }
        }
      });

      if (__DEV__) console.log('[ProjectModel] (manageGroups) Groups managed successfully');
    },
  }))
  .actions(self => {
    let reactions: IReactionDisposer[] = [];

    return {
      afterCreate() {
        // Init reactions
        const selectedProductsReaction = reaction(
          () => ({
            selectedProducts: self.selectedProducts,
          }),
          newValue => {
            if (__DEV__) console.log(`[ProjectModel] (selectedProductsReaction) value changed in: `, newValue);
            if (newValue.selectedProducts) {
              self.awaitSdkProject().then(() => self.updateProductsOptionsDebounced(newValue.selectedProducts));
            }
          },
          { name: 'selectedProductsReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(selectedProductsReaction);

        const groupsManagementReaction = reaction(
          () => ({
            groups: self.groups.map(g => ({
              id: g.id,
              isEmpty: g.isEmpty,
            })),
          }),
          newValue => {
            if (__DEV__) console.log(`[ProjectModel] (groupsManagementReaction) value changed in: `, newValue);
            self.manageGroups();
          },
          { name: 'groupsManagementReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(groupsManagementReaction);

        const productsCountReaction = reaction(
          () => self.products.length,
          productsCount => {
            if (__DEV__)
              console.log(
                `[ProjectModel] (productsCountReaction) productsCount: ${productsCount}, isProductSetProject: ${self.isProductSetProject}`,
              );
            if (self.isProductSetProject && productsCount > 0) {
              self.awaitSdkProject().then(() =>
                self.getUpgradeSetInfo().then(info => {
                  if (__DEV__) console.log('[ProjectModel] (productsCountReaction) upgradeSetInfo:', info);
                  self.setUpgradeSetInfo(info);
                }),
              );
            } else {
              self.setUpgradeSetInfo(undefined);
            }
          },
          { name: 'productsCountReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(productsCountReaction);

        const productConfigurationIdReaction = reaction(
          () => self.root.project.productConfigurationId,
          productConfigurationId => {
            if (__DEV__)
              console.log(
                `[ProjectModel] (productConfigurationIdReaction) productConfigurationId: ${productConfigurationId}`,
              );
            if (!self.root.project.isNewEditor && self.root.project?.productConfigurationId) {
              // only in the old editor we need to get the frame config steps data
              self.getFrameConfigStepsData(self.root.project.productConfigurationId);
            }
          },
          { name: 'productConfigurationIdReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(productConfigurationIdReaction);

        if (__DEV__) console.log('[ProjectModel] reactions ready: ', reactions.length);
      },

      beforeDestroy() {
        // Remove all reactions before destruction
        reactions.forEach(dispose => dispose());
        reactions = [];
        if (__DEV__) console.log('[ProjectModel] all reactions removed.');
      },
    };
  });
